import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import BrandLogo from '../components/brand/BrandLogo';

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  const isActive = (path) => location.pathname === path;

  const navLink = (to, label) => (
    <Link
      to={to}
      className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
        isActive(to)
          ? 'text-[#09b8a9] bg-[#09b8a9]/5'
          : 'text-[#383b50] hover:text-[#09b8a9] hover:bg-[#09b8a9]/5'
      }`}
    >
      {label}
    </Link>
  );

  return (
    <nav className="bg-[#fcfefe]/95 backdrop-blur-md shadow-sm border-b border-[#e5edec] sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-[4.5rem] sm:h-20 items-center justify-between gap-4">
          {/* Logo */}
          <div className="flex shrink-0 items-center">
            <Link to="/home" className="flex items-center" aria-label="Tauris home">
              <BrandLogo height={56} responsive className="h-11 sm:h-12 md:h-14" />
            </Link>
          </div>

          {/* Desktop Nav */}
          <div className="hidden min-w-0 md:flex items-center justify-center gap-0.5 lg:gap-1">
            {navLink('/home', 'Home')}
            <a
              href="/home#features"
              className="px-3 py-2 rounded-lg text-sm font-medium text-[#383b50] transition-colors duration-200 hover:bg-[#09b8a9]/5 hover:text-[#09b8a9]"
            >
              Features
            </a>
            {navLink('/pricing', 'Pricing')}
            {navLink('/community', 'Community')}
            {navLink('/contact', 'Contact')}
          </div>

          {/* Desktop CTA — legal links live in Footer to avoid header crowding */}
          <div className="hidden shrink-0 md:flex items-center gap-2 lg:gap-3">
            <Link
              to="/signin"
              className="whitespace-nowrap px-3 py-2 text-sm font-medium text-[#383b50] hover:text-[#09b8a9] transition-colors rounded-lg hover:bg-[#09b8a9]/5 lg:px-4"
            >
              Sign In
            </Link>
            <Link
              to="/signup"
              className="whitespace-nowrap px-4 py-2 text-sm font-semibold text-white bg-[#09b8a9] rounded-lg hover:bg-[#07a295] transition-all shadow-sm hover:shadow-md lg:px-5"
            >
              Start Free Trial
            </Link>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="p-2 rounded-lg text-[#383b50] hover:text-[#09b8a9] hover:bg-[#09b8a9]/5 transition-colors"
              aria-label="Toggle menu"
            >
              {isOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="md:hidden border-t border-[#e5edec] bg-[#fcfefe]"
          >
            <div className="px-4 py-3 space-y-1">
              {[
                ['/home', 'Home'],
                ['/home#features', 'Features'],
                ['/pricing', 'Pricing'],
                ['/community', 'Community'],
                ['/contact', 'Contact'],
              ].map(([to, label]) => (
                to.includes('#') ? (
                  <a
                    key={to}
                    href={to}
                    onClick={() => setIsOpen(false)}
                    className="block rounded-lg px-3 py-2.5 text-sm font-medium text-[#383b50] transition-colors hover:bg-[#09b8a9]/5 hover:text-[#09b8a9]"
                  >
                    {label}
                  </a>
                ) : (
                  <Link
                    key={to}
                    to={to}
                    onClick={() => setIsOpen(false)}
                    className={`block px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                      isActive(to)
                        ? 'text-[#09b8a9] bg-[#09b8a9]/5'
                        : 'text-[#383b50] hover:text-[#09b8a9] hover:bg-[#09b8a9]/5'
                    }`}
                  >
                    {label}
                  </Link>
                )
              ))}
              <div className="border-t border-[#e5edec] pt-3 mt-3 space-y-2">
                <Link
                  to="/signin"
                  onClick={() => setIsOpen(false)}
                  className="block text-center px-4 py-2.5 text-sm font-medium text-[#383b50] hover:text-[#09b8a9] border border-[#e5edec] rounded-lg hover:bg-[#09b8a9]/5 transition-colors"
                >
                  Sign In
                </Link>
                <Link
                  to="/signup"
                  onClick={() => setIsOpen(false)}
                  className="block text-center px-4 py-2.5 text-sm font-semibold text-white bg-[#09b8a9] rounded-lg hover:bg-[#07a295] transition-all"
                >
                  Start Free Trial
                </Link>
              </div>
              <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2 border-t border-[#e5edec] pt-3">
                {[
                  ['/terms', 'Terms'],
                  ['/privacy', 'Privacy'],
                  ['/refund', 'Refunds'],
                ].map(([to, label]) => (
                  <Link
                    key={to}
                    to={to}
                    onClick={() => setIsOpen(false)}
                    className={`text-xs font-medium transition-colors ${
                      isActive(to) ? 'text-[#09b8a9]' : 'text-[#8a93a6] hover:text-[#09b8a9]'
                    }`}
                  >
                    {label}
                  </Link>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}
